"use client";

/**
 * Shared license-acceptance dialog rendered before any asset download —
 * extracted from `AssetPurchaseCard` and `MobilePurchaseBar`, which used to
 * each ship their own near-identical copy.
 *
 * Legal text decision (per docs/page-audit/01-acquisition.md #4):
 *
 *   The two prior copies diverged. The mobile bar's bullet list dropped the
 *   "lost revenue / legal fees / statutory penalties" detail clause and the
 *   "Swedish law" jurisdiction clause entirely, and its checkbox label
 *   omitted the explicit liability acknowledgement. That mismatch is itself
 *   a legal-text bug: the obligations users are bound to should not depend
 *   on which screen size they downloaded from.
 *
 *   This component standardises on the **desktop wording** (five-bullet
 *   list, full damages detail, Swedish-law clause, full checkbox label).
 *   That copy was the more conservative / more complete of the two, so
 *   harmonising in this direction can only tighten the contract, not loosen
 *   it for any pre-existing user.
 *
 * The `idPrefix` prop preserves the per-surface checkbox `id` values
 * (`downloadLicenseAgreement` for the desktop card, `mobileLicenseAgreement`
 * for the bar) so existing analytics / e2e selectors keep matching.
 */
import Link from "next/link";
import Download from "lucide-react/dist/esm/icons/download";
import Shield from "lucide-react/dist/esm/icons/shield";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const LICENSE_LABELS: Record<string, string> = {
  personal_use: "Personal Use Only",
  sharing_allowed: "Sharing Allowed",
  uefn_map_use: "UEFN Map Use",
  revenue_share: "Revenue Share",
  custom: "Custom License",
};

export function getLicenseLabel(licenseType: string | null | undefined): string {
  return LICENSE_LABELS[licenseType || "personal_use"] || "Personal Use Only";
}

export interface LicenseAgreementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The asset's `license_type` value — falls back to "personal_use". */
  licenseType: string | null | undefined;
  agreed: boolean;
  onAgreedChange: (agreed: boolean) => void;
  onConfirm: () => void;
  /** Disambiguates the checkbox `id` across the two consumer surfaces so
   *  rendering both at once (e.g. during responsive layout transitions)
   *  doesn't break label/input pairing. */
  idPrefix?: string;
}

export function LicenseAgreementDialog({
  open,
  onOpenChange,
  licenseType,
  agreed,
  onAgreedChange,
  onConfirm,
  idPrefix = "downloadLicenseAgreement",
}: LicenseAgreementDialogProps) {
  const licenseLabel = getLicenseLabel(licenseType);
  const checkboxId = idPrefix;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            License Agreement
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                This asset is licensed under{" "}
                <strong className="text-foreground">{licenseLabel}</strong>. By
                downloading, you enter a binding agreement.
              </p>
              <div className="rounded-md border border-border bg-muted/50 p-3 text-xs space-y-2">
                <p className="font-semibold text-foreground">You agree that:</p>
                <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
                  <li>
                    You will <strong className="text-foreground">not</strong>{" "}
                    redistribute, resell, or share the downloaded files
                  </li>
                  <li>
                    You will <strong className="text-foreground">not</strong>{" "}
                    include this asset in any asset pack, template, or competing
                    product
                  </li>
                  <li>
                    You will <strong className="text-foreground">not</strong>{" "}
                    claim authorship of the original work
                  </li>
                  <li>
                    Any breach makes you{" "}
                    <strong className="text-foreground">
                      personally liable for all damages
                    </strong>
                    , including lost revenue, legal fees, and statutory penalties
                  </li>
                  <li>
                    The seller and platform reserve the right to pursue legal
                    action under{" "}
                    <strong className="text-foreground">Swedish law</strong>
                  </li>
                </ul>
              </div>
              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id={checkboxId}
                  checked={agreed}
                  onCheckedChange={(checked) => onAgreedChange(checked === true)}
                  className="mt-0.5"
                />
                <label
                  htmlFor={checkboxId}
                  className="text-xs cursor-pointer leading-relaxed text-muted-foreground"
                >
                  I have read and agree to the{" "}
                  <Link
                    href="/licenses"
                    target="_blank"
                    className="text-primary hover:underline"
                  >
                    license terms
                  </Link>
                  . I understand I will be held liable for damages if I breach
                  this agreement.
                </label>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={!agreed}>
            <Download className="h-4 w-4 mr-2" />
            Agree & Download
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
